import { Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, ViewChild } from '@angular/core';

import { CategorySearch, DEFAULT_CATEGORIES, normaliseCategory, sameCategory, searchCategories } from '../categories';

export type CategorySource = 'popular' | 'yours' | 'search' | 'added';

/**
 * The Celebrating sheet's content (plan §4): a search field that doubles as
 * "add your own", the host's own past categories, and the popular list.
 * Tapping anything picks it — there is no Done.
 */
@Component({
  selector: 'app-category-sheet',
  templateUrl: './category-sheet.component.html',
  styleUrls: ['./category-sheet.component.scss'],
})
export class CategorySheetComponent implements OnInit, OnChanges {
  /** The current category, to tick. */
  @Input() value = '';
  /** The host's own categories (past events, plus ones added on this device). */
  @Input() yours: string[] = [];

  @Output() picked = new EventEmitter<{ category: string; source: CategorySource }>();

  @ViewChild('searchInput', { static: true }) searchInput!: ElementRef<HTMLInputElement>;

  query = '';
  results: CategorySearch = { yours: [], popular: DEFAULT_CATEGORIES, addLabel: null };

  ngOnInit(): void {
    this.refresh();
  }

  ngOnChanges(): void {
    this.refresh();
  }

  onQuery(q: string): void {
    this.query = q;
    this.refresh();
  }

  isSelected(label: string): boolean {
    return !!this.value && sameCategory(this.value, label);
  }

  pick(label: string, source: CategorySource): void {
    this.picked.emit({ category: label, source: this.query.trim() && source !== 'added' ? 'search' : source });
  }

  add(): void {
    if (!this.results.addLabel) return;
    this.picked.emit({ category: normaliseCategory(this.results.addLabel), source: 'added' });
  }

  /** Enter in the search field: the one exact/only match, or add what was typed. */
  onEnter(event: Event): void {
    event.preventDefault();
    const q = this.query.trim();
    if (!q) return;
    const exact = [...this.results.yours, ...this.results.popular.map(c => c.label)].find(l => sameCategory(l, q));
    if (exact) this.pick(exact, 'search');
    else if (this.results.addLabel) this.add();
    else if (this.results.popular.length + this.results.yours.length === 1) {
      this.pick(this.results.yours[0] || this.results.popular[0].label, 'search');
    }
  }

  trackByLabel(_: number, item: { label: string } | string): string {
    return typeof item === 'string' ? item : item.label;
  }

  private refresh(): void {
    this.results = searchCategories(this.query, this.yours);
  }
}
